import { useCallback, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import { esClaveExterna, mapaDeNombres } from '../../lib/autoria'
import type { Chat, PersonaVista, TeamMember } from '../../lib/types'

// Utilidades del módulo chat: resolución de nombres, títulos de conversación,
// separadores por día, render de @menciones y un toast mínimo local.

export function useTeamRoster(): TeamMember[] {
  const { data } = useQuery({
    queryKey: qk.team,
    queryFn: () => apiGet<{ members: TeamMember[] }>('/api/team'),
  })
  return data?.members ?? []
}

// Fotos de Google del roster, por correo. /api/team ya las expone en photoUrl.
export function buildPhotoMap(team: TeamMember[]): Map<string, string | null> {
  const m = new Map<string, string | null>()
  for (const t of team) m.set(t.email, t.photoUrl ?? null)
  return m
}

// Con `personas` (el directorio histórico de /api/team, D-25) el mapa también
// nombra a quien escribió en Basecamp y no tiene cuenta. El roster gana siempre.
// Para pintar un autor con su espejo y su marca de externo, usa useAutor().
export function buildNameMap(team: TeamMember[], personas: PersonaVista[] = []): Map<string, string> {
  return mapaDeNombres(team, personas)
}

export const nameOf = (names: Map<string, string>, email: string): string =>
  names.get(email) ?? names.get(email.toLowerCase()) ?? (esClaveExterna(email) ? 'Externo' : (email.split('@')[0] ?? email))

// Etiqueta para el chip de @mención: nombre del roster; si no está, la parte
// local del correo capitalizada (nunca el correo completo en mayúsculas, ruidoso).
export function mentionLabel(names: Map<string, string>, email: string): string {
  const name = names.get(email)
  if (name) return name
  const local = email.split('@')[0] || email
  return local.charAt(0).toUpperCase() + local.slice(1)
}

export const dmOtherOf = (chat: Chat, meEmail: string): string =>
  chat.members.find((m) => m !== meEmail) ?? meEmail

export function chatTitle(
  chat: Chat,
  meEmail: string,
  names: Map<string, string>,
  projectNames: Map<string, string>,
): string {
  if (chat.type === 'group') return chat.name || 'Grupo'
  if (chat.type === 'project') return projectNames.get(chat.projectId ?? '') ?? 'Proyecto'
  const other = dmOtherOf(chat, meEmail)
  return other === meEmail ? `${nameOf(names, meEmail)} (notas)` : nameOf(names, other)
}

// ---- fechas ----

export const dayKey = (iso: string): string => new Date(iso).toDateString()

export function dayLabel(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  if (d.toDateString() === today.toDateString()) return 'hoy'
  const yesterday = new Date(today.getTime() - 86_400_000)
  if (d.toDateString() === yesterday.toDateString()) return 'ayer'
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'short' })
}

export const timeOf = (iso: string): string =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })

// ---- menciones ----

// Pinta @Nombre / @email (validadas por la API en msg.mentions) como .chip.mention.
export function renderMessageText(
  text: string,
  mentions: string[],
  names: Map<string, string>,
): ReactNode {
  if (mentions.length === 0 || !text.includes('@')) return text
  // Cada token (@email y @nombre) lleva su email canónico para resolver el
  // NOMBRE a mostrar y el tooltip, sin cambiar el texto guardado (sigue @email).
  const tokens: { token: string; email: string }[] = []
  for (const email of mentions) {
    tokens.push({ token: `@${email}`, email })
    const n = names.get(email)
    if (n) tokens.push({ token: `@${n}`, email })
  }
  tokens.sort((a, b) => b.token.length - a.token.length) // greedy: token más largo primero
  const lower = text.toLowerCase()
  const parts: ReactNode[] = []
  let i = 0
  let cursor = 0
  let key = 0
  while (i < lower.length) {
    if (lower[i] === '@') {
      const hit = tokens.find((t) => lower.startsWith(t.token.toLowerCase(), i))
      if (hit) {
        if (i > cursor) parts.push(text.slice(cursor, i))
        parts.push(
          <span key={key++} className="chip mention" title={hit.email}>
            @{mentionLabel(names, hit.email)}
          </span>,
        )
        i += hit.token.length
        cursor = i
        continue
      }
    }
    i++
  }
  if (cursor === 0) return text
  if (cursor < text.length) parts.push(text.slice(cursor))
  return parts
}

// ---- toast mínimo (local al módulo; no hay sistema global de toasts) ----

export function useToast(): [ReactNode, (msg: string) => void] {
  const [msg, setMsg] = useState<string | null>(null)
  const timer = useRef<number | null>(null)
  const show = useCallback((m: string) => {
    setMsg(m)
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setMsg(null), 3500)
  }, [])
  const node = msg ? (
    <div className="chat-toast" role="status">
      {msg}
    </div>
  ) : null
  return [node, show]
}

/**
 * Icono de la fila de un chat DE PROYECTO en la bandeja (donde un DM o un grupo
 * pintan avatar). Era el emoji 🔗 escrito como texto: rompe la regla de la casa
 * ("nunca emojis en la UI"), se pinta con la fuente de emoji del sistema — así que
 * ni hereda el color ni respeta el trazo del resto de los iconos — y además decía
 * "enlace", que no es lo que la fila es. Ahora es el MISMO panal de tres celdas que
 * usa la carpeta "Proyectos" del rail (FolderRail), para que la fila y su carpeta se
 * lean como la misma cosa.
 */
export function IconoChatProyecto() {
  return (
    <span className="chat-item-ico" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <path d="M8.5 3.5h-4l-2 3.5 2 3.5h4l2-3.5z" />
        <path d="M19.5 3.5h-4l-2 3.5 2 3.5h4l2-3.5z" />
        <path d="M14 13.5h-4l-2 3.5 2 3.5h4l2-3.5z" />
      </svg>
    </span>
  )
}
